import { Component, Inject, OnInit, OnDestroy, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';

export const FIELD_LABEL_MAP: Record<string, string> = {
  // Vessel & General Info
  shipName: 'Ship Name',
  vesselName: 'Ship Name',
  imoNumber: 'IMO Number',
  vesselIMO: 'IMO Number',
  shipType: 'Type of Ship',
  vesselType: 'Type of Ship',
  segment: 'Segment',
  vesselSegment: 'Segment',
  condition: 'Condition',
  vesselCondition: 'Condition',
  fromPort: 'From Port',
  departurePort: 'From Port',
  toPort: 'To Port',
  destinationPort: 'To Port',
  voyageNumber: 'Voyage Number',
  date: 'Operation Date',
  reportDate: 'Operation Date',
  reportHour: 'Operation Hour (UTC)',
  reportMinute: 'Operation Minute (UTC)',
  report_timestamp: 'Report Timestamp (UTC)',
  timezone_offset: 'Time Zone',
  timezoneOffset: 'Time Zone',
  portTimeZone: 'Port Time Zone',
  eventsList: 'Operations',
  operations: 'Operations in Port',

  // ETA & ETD
  etaOrderMerged: 'ETA Next Port (C/P Ordered)',
  etaOrderDate: 'ETA Next Port C/P Ordered Date',
  etaOrderHour: 'ETA Next Port C/P Ordered Hour',
  etaOrderMinute: 'ETA Next Port C/P Ordered Minute',
  etaWeatherMerged: 'ETA Next Port (Projected Weather)',
  etaWeatherDate: 'ETA Next Port Projected Weather Date',
  etaWeatherHour: 'ETA Next Port Projected Weather Hour',
  etaWeatherMinute: 'ETA Next Port Projected Weather Minute',
  etaWeatherLthour: 'ETA Next Port Projected Weather LT Hour',
  etaWeatherLtminute: 'ETA Next Port Projected Weather LT Minute',
  etdCurrentPortMerged: 'ETD Current Port',
  etdCurrentPortDate: 'ETD Current Port Date',
  etdCurrentPortHour: 'ETD Current Port Hour',
  etdCurrentPortMinute: 'ETD Current Port Minute',
  reportDateTimeMerged: 'Operation Date & Time (UTC)',
  chartererName: "Charterer's Name",

  // Cargo & Draft
  reeferCount: 'Number of Reefer Containers on Board',
  reeferKwh: 'Reefer kWhr',
  forwardDraftValue: 'Forward Draft',
  afterDraftValue: 'After Draft',
  displacementValue: 'Displacement',
  slopsRob: 'Slops Volume (m³)',
  slopsTank: 'Slops Location - Slop (p) / Slop (s) / ROT',
  cargoQty: 'Cargo Quantity',

  // Position & Navigation
  latDegree: 'Latitude Degree',
  latitudeDegree: 'Latitude Degree',
  latMin: 'Latitude Minute',
  latitudeMin: 'Latitude Minute',
  latDir: 'Latitude Direction',
  latitudeDir: 'Latitude Direction',
  latitudeResult: 'Latitude',
  lngDegree: 'Longitude Degree',
  longitudeDegree: 'Longitude Degree',
  lngMin: 'Longitude Minute',
  longitudeMin: 'Longitude Minute',
  lngDir: 'Longitude Direction',
  longitudeDir: 'Longitude Direction',
  longitudeResult: 'Longitude',

  // Speed & Distance
  cpOrderedSpeed: 'C/P Ordered Speed (Knots)',
  contractualSpeed: 'C/P Ordered Speed (Knots)',
  cpOrderedConsumptionFo: 'C/P Ordered Consumption FO (ME+AE)',
  contractualConsumpFo: 'C/P Ordered Consumption FO (ME+AE)',
  cpOrderedConsumptionGo: 'C/P Ordered Consumption GO (AE)',
  contractualConsumpGo: 'C/P Ordered Consumption GO (AE)',
  distanceToGo: 'Distance To Go',
  remainingDistance: 'Distance To Go',
  steamingDistance: 'Steaming Distance Since Last Report (GPS)',
  steamingDistanceGps: 'Steaming Distance Since Last Report (GPS)',
  steamingHours: 'Steaming Hours Since Last Report',
  steamingHoursReport: 'Steaming Hours Since Last Report',
  avgSpeed: 'Average Speed Since Last Report',
  avgSpeedReport: 'Average Speed Since Last Report',
  logDistance: 'Log Distance Since Last Report',
  logDistanceReport: 'Log Distance Since Last Report',
  logSpeed: 'Log Speed Since Last Report',
  logSpeedReport: 'Log Speed Since Last Report',
  avgShipsCourse: 'Average Ships Course Since Last Report',
  avgCourseReport: 'Average Ships Course Since Last Report',
  totalDistanceObserved: 'Total Distance Observed (GPS)',
  totalDistanceGps: 'Total Distance Observed (GPS)',
  totalHours: 'Total Hours',
  totalVoyageHours: 'Total Hours',
  overallAvgSpeed: 'Overall Avg. Speed',
  overallAverageSpeed: 'Overall Avg. Speed',

  // Weather & Sea
  trueWindDirection: 'True Wind Direction',
  trueWind: 'True Wind (Knots)',
  trueWindKnots: 'True Wind (Knots)',
  windForceBfScale: 'Wind Force (BF Scale)',
  windForceBf: 'Wind Force (BF Scale)',
  windDescription: 'Wind Description',
  windDescriptionSelect: 'Wind Description',
  relativeWindSpeed: 'Relative Wind Speed (Knots)',
  relativeWindSpeedKnots: 'Relative Wind Speed (Knots)',
  relativeWindDirection: 'Relative Wind Direction (deg)',
  relativeWindDirectionDegrees: 'Relative Wind Direction (deg)',
  swellDirection: 'True Swell Direction',
  swellHeight: 'Swell Height (Meter)',
  swellHeightMeter: 'Swell Height (Meter)',
  seaHeight: 'Sea Height (Meter)',
  seaHeightMeter: 'Sea Height (Meter)',
  trueSeaDirection: 'True Sea Direction',
  currentDirection: 'Current Direction',
  currentDirectionSelect: 'Current Direction',
  current: 'Current (Knots)',
  currentKnots: 'Current (Knots)',
  airTemp: 'Air Temperature (°C)',
  airTemperature: 'Air Temperature (°C)',
  seaTemp: 'Sea Temperature (°C)',
  seaTemperature: 'Sea Temperature (°C)',
  pressure: 'Pressure (mbar)',
  pressureMbar: 'Pressure (mbar)',

  // Engine Room Log & Machinery
  mainEngineRpm: 'Main Engine RPM',
  meRevolutionCounter: 'Engine M/E Revolution Counter Last Report',
  engineAverageBhp: 'Main Engine Avg kW',
  engineLoad: 'Main Engine Load %',
  engineLoadPercent: 'Main Engine Load %',
  totalShaftPower: 'Total Shaft Power From Last Report (kWh)',
  engineLogDistance: 'Engine Distance',
  engineSpeed: 'Engine Speed',
  slip: 'Slip (%)',
  slipPercentage: 'Slip (%)',
  meRunningHours: 'M/E Running Hrs Since Last Report',
  erTemp: 'ER Temp (°C)',
  scavengeAirPress: 'Scavenge Air Press (bar)',
  scavengeAirTemp: 'Scavenge Air Temp (°C)',
  airCoolerTemp: 'Air Cooler CW Inlet Temp (°C)',
  auxEngineOne: 'Auxiliary Engine 1',
  auxEngineOneHours: 'Auxiliary Engine 1 - Hours Since Last Noon',
  auxEngineOneKw: 'Auxiliary Engine 1 - Power (kW)',
  auxEngineTwo: 'Auxiliary Engine 2',
  auxEngineTwoHours: 'Auxiliary Engine 2 - Hours Since Last Noon',
  auxEngineTwoKw: 'Auxiliary Engine 2 - Power (kW)',
  auxEngineThree: 'Auxiliary Engine 3',
  auxEngineThreeHours: 'Auxiliary Engine 3 - Hours Since Last Noon',
  auxEngineThreeKw: 'Auxiliary Engine 3 - Power (kW)',
  hydraulicSysPressure: 'Hydraulic System Avg Pressure (bar)',
  copOneRpm: 'COP 1 Avg RPM',
  copTwoRpm: 'COP 2 Avg RPM',
  copThreeRpm: 'COP 3 Avg RPM',

  // Fuel Consumptions & ROBs
  bunkerGrade1: 'Bunker Received (1)',
  quantity1: 'Bunker Quantity 1 (MT)',
  descrepency1: 'Bunker Discrepancy 1 (MT)',
  remark1: 'Bunker Remark 1',
  bunkerGrade2: 'Bunker Received (2)',
  quantity2: 'Bunker Quantity 2 (MT)',
  descrepency2: 'Bunker Discrepancy 2 (MT)',
  remark2: 'Bunker Remark 2',
  bunkerGrade3: 'Bunker Received (3)',
  quantity3: 'Bunker Quantity 3 (MT)',
  descrepency3: 'Bunker Discrepancy 3 (MT)',
  remark3: 'Bunker Remark 3',

  mgoMeOldRob: 'HSFO Old ROB',
  mgoAeOldRob: 'VLSFO Old ROB',
  mgoBlrOldRob: 'ULSFO Old ROB',
  mgoTotalOldRob: 'MGO Total Old ROB',
  lngTotalOldRob: 'LNG Total Old ROB',
  'me-consumption': 'M/E Consmp Since Last Report (MT)',
  'ae-consumption': 'A/E Consmp Since Last Report',
  'others-type': 'Others Consmp Since Last Report',
  'others-quantity': 'Others Consumption Quantity',
  mgoMeConsumption: 'MGO M/E Consmp Since Last Report',
  mgoAeConsumption: 'MGO A/E Consmp Since Last Report',
  mgoBlrConsumption: 'MGO Boiler Consmp Since Last Report',
  mgoOthersConsumptionType: 'MGO Others Consmp Since Last Report',
  mgoOthersConsumptionQuantity: 'Others Consumption Quantity',
  otherDate: 'Date of Other Consumption',
  mgoReportDate: 'Date of Other Consumption',
  otherHour: 'Duration of Other Consumption (Hrs)',
  mgoReportHour: 'Duration of Other Consumption (Hrs)',
  otherMinute: 'Duration of Other Consumption (Min)',
  mgoReportMinute: 'Duration of Other Consumption (Min)',
  gasOilPowerPack: 'Gas Oil Power Pack (Cargo Ops)',
  incineratorGasOil: 'Incinerator Gas Oil',
  inertingGasOil: 'Inerting Gas Oil',
  mgoTotalConsumption: 'MGO Total Consumption (MT)',
  mgoBunkerRob: 'MGO Bunker ROB Current',

  // Miscellaneous
  freshWaterRob: 'Freshwater Quantity On Board (MT)',
  distilledWaterQty: 'Distilled Water Quantity on Board (MT)',
  voyageRemarks: 'Remarks',
};

export interface ReportField {
  key: string;
  label: string;
  value: any;
}

export interface ReportCategory {
  id: string;
  title: string;
  icon: string;
  fields: ReportField[];
}

/**
 * Strict set of fields visible on Master Report Form v2.4.1 (Harar).
 * Anything not in this set (e.g. errorMailDetails, errorMailDrafted, audit metadata) will NOT be shown.
 */
export const VALID_HTML_FORM_FIELDS = new Set<string>([
  'shipName', 'vesselName',
  'imoNumber', 'vesselIMO',
  'shipType', 'vesselType',
  'segment', 'vesselSegment',
  'condition', 'vesselCondition',
  'fromPort', 'departurePort',
  'toPort', 'destinationPort',
  'voyageNumber',
  'date', 'reportDate',
  'reportHour',
  'reportMinute',
  'report_timestamp',
  'reportDateTimeMerged',
  'portTimeZone',
  'eventsList',
  'operations',
  'etaOrderMerged',
  'etaOrderDate', 'etaOrderHour', 'etaOrderMinute',
  'etaWeatherMerged',
  'etaWeatherDate', 'etaWeatherHour', 'etaWeatherMinute', 'etaWeatherLthour', 'etaWeatherLtminute',
  'etdCurrentPortMerged',
  'etdCurrentPortDate', 'etdCurrentPortHour', 'etdCurrentPortMinute',
  'chartererName',

  'reeferCount',
  'reeferKwh',
  'forwardDraftValue',
  'afterDraftValue',
  'displacementValue',
  'slopsRob',
  'slopsTank',
  'cargoQty',

  'latDegree', 'latitudeDegree',
  'latMin', 'latitudeMin',
  'latDir', 'latitudeDir',
  'latitudeResult',
  'lngDegree', 'longitudeDegree',
  'lngMin', 'longitudeMin',
  'lngDir', 'longitudeDir',
  'longitudeResult',
  'cpOrderedSpeed', 'contractualSpeed',
  'cpOrderedConsumptionFo', 'contractualConsumpFo',
  'cpOrderedConsumptionGo', 'contractualConsumpGo',
  'distanceToGo', 'remainingDistance',
  'steamingDistance', 'steamingDistanceGps',
  'steamingHours', 'steamingHoursReport',
  'avgSpeed', 'avgSpeedReport',
  'logDistance', 'logDistanceReport',
  'logSpeed', 'logSpeedReport',
  'avgShipsCourse', 'avgCourseReport',
  'totalDistanceObserved', 'totalDistanceGps',
  'totalHours', 'totalVoyageHours',
  'overallAvgSpeed', 'overallAverageSpeed',

  'trueWindDirection',
  'trueWind', 'trueWindKnots',
  'windForceBfScale', 'windForceBf',
  'windDescription', 'windDescriptionSelect',
  'relativeWindSpeed', 'relativeWindSpeedKnots',
  'relativeWindDirection', 'relativeWindDirectionDegrees',
  'swellDirection',
  'swellHeight', 'swellHeightMeter',
  'seaHeight', 'seaHeightMeter',
  'trueSeaDirection',
  'currentDirection', 'currentDirectionSelect',
  'current', 'currentKnots',
  'airTemp', 'airTemperature',
  'seaTemp', 'seaTemperature',
  'pressure', 'pressureMbar',

  'mainEngineRpm',
  'meRevolutionCounter',
  'engineAverageBhp',
  'engineLoad', 'engineLoadPercent',
  'totalShaftPower',
  'engineLogDistance',
  'engineSpeed',
  'slip', 'slipPercentage',
  'meRunningHours',
  'erTemp',
  'scavengeAirPress',
  'scavengeAirTemp',
  'airCoolerTemp',
  'auxEngineOne', 'auxEngineOneHours', 'auxEngineOneKw',
  'auxEngineTwo', 'auxEngineTwoHours', 'auxEngineTwoKw',
  'auxEngineThree', 'auxEngineThreeHours', 'auxEngineThreeKw',
  'hydraulicSysPressure',
  'copOneRpm', 'copTwoRpm', 'copThreeRpm',

  'bunkerGrade1', 'quantity1', 'descrepency1', 'remark1',
  'bunkerGrade2', 'quantity2', 'descrepency2', 'remark2',
  'bunkerGrade3', 'quantity3', 'descrepency3', 'remark3',
  'mgoMeOldRob',
  'mgoAeOldRob',
  'mgoBlrOldRob',
  'mgoTotalOldRob',
  'lngTotalOldRob',
  'me-consumption', 'mgoMeConsumption',
  'ae-consumption', 'mgoAeConsumption',
  'mgoBlrConsumption',
  'others-type', 'mgoOthersConsumptionType',
  'others-quantity', 'mgoOthersConsumptionQuantity',
  'otherDate', 'mgoReportDate',
  'otherHour', 'mgoReportHour',
  'otherMinute', 'mgoReportMinute',
  'mgoTotalConsumption',
  'mgoBunkerRob',
  'gasOilPowerPack',
  'incineratorGasOil',
  'inertingGasOil',

  'freshWaterRob',
  'distilledWaterQty',
  'voyageRemarks',
]);

const ALIAS_MAP: Record<string, string[]> = {
  shipName: ['vesselName'],
  vesselName: ['shipName'],
  imoNumber: ['vesselIMO'],
  vesselIMO: ['imoNumber'],
  shipType: ['vesselType'],
  vesselType: ['shipType'],
  segment: ['vesselSegment'],
  vesselSegment: ['segment'],
  condition: ['vesselCondition'],
  vesselCondition: ['condition'],
  fromPort: ['departurePort'],
  departurePort: ['fromPort'],
  toPort: ['destinationPort'],
  destinationPort: ['toPort'],
  date: ['reportDate'],
  reportDate: ['date'],
  latDegree: ['latitudeDegree'],
  latitudeDegree: ['latDegree'],
  latMin: ['latitudeMin'],
  latitudeMin: ['latMin'],
  latDir: ['latitudeDir'],
  latitudeDir: ['latDir'],
  lngDegree: ['longitudeDegree'],
  longitudeDegree: ['lngDegree'],
  lngMin: ['longitudeMin'],
  longitudeMin: ['lngMin'],
  lngDir: ['longitudeDir'],
  longitudeDir: ['lngDir'],
  cpOrderedSpeed: ['contractualSpeed'],
  contractualSpeed: ['cpOrderedSpeed'],
  cpOrderedConsumptionFo: ['contractualConsumpFo'],
  contractualConsumpFo: ['cpOrderedConsumptionFo'],
  cpOrderedConsumptionGo: ['contractualConsumpGo'],
  contractualConsumpGo: ['cpOrderedConsumptionGo'],
  distanceToGo: ['remainingDistance'],
  remainingDistance: ['distanceToGo'],
  steamingDistance: ['steamingDistanceGps'],
  steamingDistanceGps: ['steamingDistance'],
  steamingHours: ['steamingHoursReport'],
  steamingHoursReport: ['steamingHours'],
  avgSpeed: ['avgSpeedReport'],
  avgSpeedReport: ['avgSpeed'],
  logDistance: ['logDistanceReport'],
  logDistanceReport: ['logDistance'],
  logSpeed: ['logSpeedReport'],
  logSpeedReport: ['logSpeed'],
  avgShipsCourse: ['avgCourseReport'],
  avgCourseReport: ['avgShipsCourse'],
  totalDistanceObserved: ['totalDistanceGps'],
  totalDistanceGps: ['totalDistanceObserved'],
  totalHours: ['totalVoyageHours'],
  totalVoyageHours: ['totalHours'],
  overallAvgSpeed: ['overallAverageSpeed'],
  overallAverageSpeed: ['overallAvgSpeed'],
  trueWind: ['trueWindKnots'],
  trueWindKnots: ['trueWind'],
  windForceBfScale: ['windForceBf'],
  windForceBf: ['windForceBfScale'],
  windDescription: ['windDescriptionSelect'],
  windDescriptionSelect: ['windDescription'],
  relativeWindSpeed: ['relativeWindSpeedKnots'],
  relativeWindSpeedKnots: ['relativeWindSpeed'],
  relativeWindDirection: ['relativeWindDirectionDegrees'],
  relativeWindDirectionDegrees: ['relativeWindDirection'],
  swellHeight: ['swellHeightMeter'],
  swellHeightMeter: ['swellHeight'],
  seaHeight: ['seaHeightMeter'],
  seaHeightMeter: ['seaHeight'],
  currentDirection: ['currentDirectionSelect'],
  currentDirectionSelect: ['currentDirection'],
  current: ['currentKnots'],
  currentKnots: ['current'],
  airTemp: ['airTemperature'],
  airTemperature: ['airTemp'],
  seaTemp: ['seaTemperature'],
  seaTemperature: ['seaTemp'],
  pressure: ['pressureMbar'],
  pressureMbar: ['pressure'],
  engineLoad: ['engineLoadPercent'],
  engineLoadPercent: ['engineLoad'],
  slip: ['slipPercentage'],
  slipPercentage: ['slip'],
  'others-type': ['mgoOthersConsumptionType'],
  mgoOthersConsumptionType: ['others-type'],
  'others-quantity': ['mgoOthersConsumptionQuantity'],
  mgoOthersConsumptionQuantity: ['others-quantity'],
  otherDate: ['mgoReportDate'],
  mgoReportDate: ['otherDate'],
  otherHour: ['mgoReportHour'],
  mgoReportHour: ['otherHour'],
  otherMinute: ['mgoReportMinute'],
  mgoReportMinute: ['otherMinute'],
  timezone_offset: ['timezoneOffset', 'portTimeZone'],
  timezoneOffset: ['timezone_offset', 'portTimeZone'],
  eventsList: ['operations'],
  operations: ['eventsList'],
  report_timestamp: ['date', 'reportDate', 'reportDateTimeMerged'],
  reportDateTimeMerged: ['report_timestamp', 'date', 'reportDate'],
};

/**
 * Keys prominently featured in the modal header banner.
 * These are excluded from the section cards below so they are never displayed twice.
 */
export const HEADER_PROMOTED_KEYS = new Set<string>([
  'shipName',
  'vesselName',
  'imoNumber',
  'vesselIMO',
  'voyageNumber',
  'eventsList',
  'operations',
  'date',
  'reportDate',
  'reportHour',
  'reportMinute',
  'report_timestamp',
  'reportDateTimeMerged',
]);

/**
 * Section definitions following the exact sequence of Master Report Form v2.4.1 (Harar).
 */
export const SECTION_DEFS: { id: string; title: string; icon: string; keys: string[] }[] = [
  {
    id: 'general',
    title: 'General & Voyage Details',
    icon: 'directions_boat',
    keys: [
      'shipName', 'vesselName', 'imoNumber', 'vesselIMO', 'shipType', 'vesselType',
      'segment', 'vesselSegment', 'condition', 'vesselCondition', 'fromPort', 'departurePort',
      'toPort', 'destinationPort', 'voyageNumber', 'date', 'reportDate',
      'reportHour', 'reportMinute', 'report_timestamp', 'portTimeZone',
      'eventsList', 'operations', 'etaOrderDate', 'etaOrderHour',
      'etaOrderMinute', 'etaWeatherDate', 'etaWeatherHour', 'etaWeatherMinute', 'etaWeatherLthour',
      'etaWeatherLtminute', 'etdCurrentPortDate', 'etdCurrentPortHour', 'etdCurrentPortMinute',
      'chartererName'
    ]
  },
  {
    id: 'cargo',
    title: 'Cargo & Draft Information',
    icon: 'inventory_2',
    keys: [
      'reeferCount', 'reeferKwh', 'forwardDraftValue', 'afterDraftValue', 'displacementValue',
      'slopsRob', 'slopsTank', 'cargoQty'
    ]
  },
  {
    id: 'nautical',
    title: 'Nautical Log & Navigation',
    icon: 'explore',
    keys: [
      'latDegree', 'latitudeDegree', 'latMin', 'latitudeMin', 'latDir', 'latitudeDir',
      'latitudeResult', 'lngDegree', 'longitudeDegree', 'lngMin', 'longitudeMin', 'lngDir',
      'longitudeDir', 'longitudeResult', 'cpOrderedSpeed', 'contractualSpeed',
      'cpOrderedConsumptionFo', 'contractualConsumpFo', 'cpOrderedConsumptionGo',
      'contractualConsumpGo', 'distanceToGo', 'remainingDistance', 'steamingDistance',
      'steamingDistanceGps', 'steamingHours', 'steamingHoursReport', 'avgSpeed',
      'avgSpeedReport', 'logDistance', 'logDistanceReport', 'logSpeed', 'logSpeedReport',
      'avgShipsCourse', 'avgCourseReport', 'totalDistanceObserved', 'totalDistanceGps',
      'totalHours', 'totalVoyageHours', 'overallAvgSpeed', 'overallAverageSpeed'
    ]
  },
  {
    id: 'weather',
    title: 'Weather & Sea Conditions',
    icon: 'wb_sunny',
    keys: [
      'trueWindDirection', 'trueWind', 'trueWindKnots', 'windForceBfScale', 'windForceBf',
      'windDescription', 'windDescriptionSelect', 'relativeWindSpeed', 'relativeWindSpeedKnots',
      'relativeWindDirection', 'relativeWindDirectionDegrees', 'swellDirection', 'swellHeight',
      'swellHeightMeter', 'seaHeight', 'seaHeightMeter', 'trueSeaDirection', 'currentDirection',
      'currentDirectionSelect', 'current', 'currentKnots', 'airTemp', 'airTemperature',
      'seaTemp', 'seaTemperature', 'pressure', 'pressureMbar'
    ]
  },
  {
    id: 'engine',
    title: 'Engine Room & Machinery',
    icon: 'build',
    keys: [
      'mainEngineRpm', 'meRevolutionCounter', 'engineAverageBhp', 'engineLoad',
      'engineLoadPercent', 'totalShaftPower', 'engineLogDistance', 'engineSpeed', 'slip',
      'slipPercentage', 'meRunningHours', 'erTemp', 'scavengeAirPress', 'scavengeAirTemp',
      'airCoolerTemp', 'auxEngineOne', 'auxEngineOneHours', 'auxEngineOneKw',
      'auxEngineTwo', 'auxEngineTwoHours', 'auxEngineTwoKw', 'auxEngineThree',
      'auxEngineThreeHours', 'auxEngineThreeKw', 'hydraulicSysPressure',
      'copOneRpm', 'copTwoRpm', 'copThreeRpm'
    ]
  },
  {
    id: 'fuels',
    title: 'Fuel Consumptions & Bunkers',
    icon: 'local_gas_station',
    keys: [
      'bunkerGrade1', 'quantity1', 'descrepency1', 'remark1',
      'bunkerGrade2', 'quantity2', 'descrepency2', 'remark2',
      'bunkerGrade3', 'quantity3', 'descrepency3', 'remark3',
      'mgoMeOldRob', 'mgoAeOldRob', 'mgoBlrOldRob', 'mgoTotalOldRob', 'lngTotalOldRob',
      'mgoBunkerRob', 'me-consumption', 'mgoMeConsumption', 'ae-consumption',
      'mgoAeConsumption', 'mgoBlrConsumption', 'others-type', 'mgoOthersConsumptionType',
      'others-quantity', 'mgoOthersConsumptionQuantity', 'otherDate', 'mgoReportDate',
      'otherHour', 'mgoReportHour', 'otherMinute', 'mgoReportMinute',
      'mgoTotalConsumption', 'gasOilPowerPack', 'incineratorGasOil', 'inertingGasOil'
    ]
  },
  {
    id: 'misc',
    title: 'Miscellaneous',
    icon: 'assignment',
    keys: [
      'freshWaterRob', 'distilledWaterQty', 'voyageRemarks'
    ]
  }
];

@Component({
  selector: 'app-masterreporting-tool-view',
  templateUrl: './masterreporting-tool-view.component.html',
  styleUrls: ['./masterreporting-tool-view.component.scss'],
})
export class MasterreportingToolViewComponent implements OnInit, OnDestroy {
  showRaw = false;
  searchTerm = '';
  isDark = true;
  private themeSub?: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<MasterreportingToolViewComponent>,
    @Optional() private requestFormService?: RequestFormService
  ) {
    this.isDark = this.checkDarkMode();
  }

  ngOnInit(): void {
    if (this.requestFormService) {
      this.themeSub = this.requestFormService.themeChanged.subscribe((dark: boolean) => {
        this.isDark = dark;
      });
    }
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
  }

  checkDarkMode(): boolean {
    if (this.data && typeof this.data.isDark === 'boolean') {
      return this.data.isDark;
    }
    const pref = localStorage.getItem('darkModePreference');
    if (pref !== null) {
      try {
        return JSON.parse(pref);
      } catch (e) {}
    }
    if (this.requestFormService) {
      return this.requestFormService.isDarkMode();
    }
    return true;
  }

  toggleTheme(): void {
    this.isDark = !this.isDark;
  }

  getVesselName(): string {
    const rep = this.data?.report || {};
    const raw = rep.rawJson || {};
    return rep.vesselName || rep.shipName || raw.vesselName || raw.shipName || '';
  }

  getImo(): string {
    const rep = this.data?.report || {};
    const raw = rep.rawJson || {};
    return rep.vesselIMO || rep.imoNumber || raw.vesselIMO || raw.imoNumber || '';
  }

  getVoyageNumber(): string {
    const rep = this.data?.report || {};
    const raw = rep.rawJson || {};
    return rep.voyageNumber || raw.voyageNumber || '';
  }

  getOperations(): string {
    const rep = this.data?.report || {};
    const raw = rep.rawJson || {};
    return rep.eventsList || rep.operations || raw.eventsList || raw.operations || '';
  }

  getReportDateTime(): string {
    const rep = this.data?.report || {};
    const raw = rep.rawJson || {};
    const dateVal = rep.reportDate || rep.date || raw.reportDate || raw.date;
    const hourVal = rep.reportHour ?? raw.reportHour;
    const minVal = rep.reportMinute ?? raw.reportMinute;

    if (this.hasData(dateVal)) {
      return this.formatMergedDateTime(dateVal, hourVal, minVal);
    }
    if (this.hasData(rep.report_timestamp)) {
      return this.formatMergedDateTime(rep.report_timestamp);
    }
    return '';
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  isObject(value: any): boolean {
    return value !== null && typeof value === 'object';
  }

  /**
   * Checks whether a value has actual displayable data (not empty, null, undefined, none, etc.)
   */
  hasData(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      const s = value.trim();
      if (
        s === '' ||
        s === '-' ||
        s.toLowerCase() === 'none' ||
        s.toLowerCase() === 'null' ||
        s.toLowerCase() === 'nan' ||
        s.toLowerCase() === 'undefined'
      ) {
        return false;
      }
      return true;
    }
    if (typeof value === 'number') {
      return !isNaN(value);
    }
    if (typeof value === 'boolean') {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  }

  getLabel(key: string): string {
    if (FIELD_LABEL_MAP[key]) {
      return FIELD_LABEL_MAP[key];
    }
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]+/g, ' ')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  /**
   * Formats consolidated date, time, and optional local time into a unified string.
   */
  formatMergedDateTime(
    dateVal: any,
    hourVal?: any,
    minVal?: any,
    ltHourVal?: any,
    ltMinVal?: any
  ): string {
    const hasDate = this.hasData(dateVal);
    const hasTime = this.hasData(hourVal);

    if (!hasDate && !hasTime) {
      return '';
    }

    let dateStr = '';
    if (hasDate) {
      const raw = String(dateVal).trim();
      const match = raw.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
      if (match) {
        dateStr = `${match[1].padStart(2, '0')}-${match[2]}-${match[3]}`;
      } else {
        const d = new Date(raw);
        if (!isNaN(d.getTime())) {
          const pad = (n: number) => String(n).padStart(2, '0');
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const dd = pad(d.getUTCDate());
          const mmm = months[d.getUTCMonth()];
          const yyyy = d.getUTCFullYear();
          dateStr = `${dd}-${mmm}-${yyyy}`;
          if (!hasTime && (d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0)) {
            hourVal = d.getUTCHours();
            minVal = d.getUTCMinutes();
          }
        } else {
          dateStr = raw.split('T')[0].split(' ')[0];
        }
      }
    }

    const pad = (n: any) => String(n ?? 0).padStart(2, '0');

    let timeStr = '';
    if (this.hasData(hourVal)) {
      const hh = pad(hourVal);
      const mm = this.hasData(minVal) ? pad(minVal) : '00';
      timeStr = `${hh}:${mm} UTC`;
    }

    let result = '';
    if (dateStr && timeStr) {
      result = `${dateStr} ${timeStr}`;
    } else if (dateStr) {
      result = dateStr;
    } else {
      result = timeStr;
    }

    if (this.hasData(ltHourVal)) {
      const ltHh = pad(ltHourVal);
      const ltMm = this.hasData(ltMinVal) ? pad(ltMinVal) : '00';
      result += ` (LT: ${ltHh}:${ltMm})`;
    }

    return result.trim();
  }

  /**
   * Handles merging of fragmented date, hour, minute parameters.
   * Returns true if the key was processed as part of a merged group.
   */
  private handleMergedDateTimeFields(
    key: string,
    dataObj: any,
    addedKeys: Set<string>,
    search: string,
    fields: ReportField[]
  ): boolean {
    if (
      key === 'etaOrderDate' ||
      key === 'etaOrderHour' ||
      key === 'etaOrderMinute'
    ) {
      if (addedKeys.has('etaOrderMerged')) return true;
      addedKeys.add('etaOrderDate');
      addedKeys.add('etaOrderHour');
      addedKeys.add('etaOrderMinute');
      addedKeys.add('etaOrderMerged');
      const mergedVal = this.formatMergedDateTime(
        dataObj.etaOrderDate,
        dataObj.etaOrderHour,
        dataObj.etaOrderMinute
      );
      if (this.hasData(mergedVal)) {
        const label = 'ETA Next Port (C/P Ordered)';
        if (!search || label.toLowerCase().includes(search) || mergedVal.toLowerCase().includes(search)) {
          fields.push({ key: 'etaOrderMerged', label, value: mergedVal });
        }
      }
      return true;
    }

    if (
      key === 'etaWeatherDate' ||
      key === 'etaWeatherHour' ||
      key === 'etaWeatherMinute' ||
      key === 'etaWeatherLthour' ||
      key === 'etaWeatherLtminute'
    ) {
      if (addedKeys.has('etaWeatherMerged')) return true;
      addedKeys.add('etaWeatherDate');
      addedKeys.add('etaWeatherHour');
      addedKeys.add('etaWeatherMinute');
      addedKeys.add('etaWeatherLthour');
      addedKeys.add('etaWeatherLtminute');
      addedKeys.add('etaWeatherMerged');
      const mergedVal = this.formatMergedDateTime(
        dataObj.etaWeatherDate,
        dataObj.etaWeatherHour,
        dataObj.etaWeatherMinute,
        dataObj.etaWeatherLthour,
        dataObj.etaWeatherLtminute
      );
      if (this.hasData(mergedVal)) {
        const label = 'ETA Next Port (Projected Weather)';
        if (!search || label.toLowerCase().includes(search) || mergedVal.toLowerCase().includes(search)) {
          fields.push({ key: 'etaWeatherMerged', label, value: mergedVal });
        }
      }
      return true;
    }

    if (
      key === 'etdCurrentPortDate' ||
      key === 'etdCurrentPortHour' ||
      key === 'etdCurrentPortMinute'
    ) {
      if (addedKeys.has('etdCurrentPortMerged')) return true;
      addedKeys.add('etdCurrentPortDate');
      addedKeys.add('etdCurrentPortHour');
      addedKeys.add('etdCurrentPortMinute');
      addedKeys.add('etdCurrentPortMerged');
      const mergedVal = this.formatMergedDateTime(
        dataObj.etdCurrentPortDate,
        dataObj.etdCurrentPortHour,
        dataObj.etdCurrentPortMinute
      );
      if (this.hasData(mergedVal)) {
        const label = 'ETD Current Port';
        if (!search || label.toLowerCase().includes(search) || mergedVal.toLowerCase().includes(search)) {
          fields.push({ key: 'etdCurrentPortMerged', label, value: mergedVal });
        }
      }
      return true;
    }

    if (
      key === 'date' ||
      key === 'reportDate' ||
      key === 'reportHour' ||
      key === 'reportMinute'
    ) {
      if (addedKeys.has('reportDateTimeMerged')) return true;
      addedKeys.add('date');
      addedKeys.add('reportDate');
      addedKeys.add('reportHour');
      addedKeys.add('reportMinute');
      addedKeys.add('reportDateTimeMerged');
      const dateVal = dataObj.reportDate ?? dataObj.date;
      const mergedVal = this.formatMergedDateTime(
        dateVal,
        dataObj.reportHour,
        dataObj.reportMinute
      );
      if (this.hasData(mergedVal)) {
        const label = 'Operation Date & Time (UTC)';
        if (!search || label.toLowerCase().includes(search) || mergedVal.toLowerCase().includes(search)) {
          fields.push({ key: 'reportDateTimeMerged', label, value: mergedVal });
        }
      }
      return true;
    }

    return false;
  }

  /**
   * Resolves the overall Cargo Quantity. If report.cargoQty is zero or missing,
   * fall back to the sum of items in cargoDetails.
   */
  getCargoQuantity(report: any): any {
    if (!report) return '-';

    const directQty = report.cargoQty ?? report.rawJson?.cargoQty;
    const directNum = parseFloat(directQty);

    // If direct cargoQty is valid and > 0, return it
    if (this.hasData(directQty) && !isNaN(directNum) && directNum > 0) {
      return directQty;
    }

    // Otherwise inspect cargoDetails array
    const details = this.getCargoDetails(report);
    if (details && details.length > 0) {
      let total = 0;
      let hasValidQty = false;
      for (const item of details) {
        const q = parseFloat(item.quantity ?? item.qty ?? item.cargoQuantity ?? 0);
        if (!isNaN(q) && q > 0) {
          total += q;
          hasValidQty = true;
        }
      }
      if (hasValidQty && total > 0) {
        return total;
      }
    }

    return this.hasData(directQty) ? directQty : '-';
  }

  /**
   * Returns list of cargo items from cargoDetails
   */
  getCargoDetails(report?: any): Array<{ name?: string; quantity?: any; qty?: any; cargoQuantity?: any }> {
    const rep = report || this.data?.report || {};
    const raw = rep.rawJson || {};
    let details = rep.cargoDetails ?? raw.cargoDetails;

    if (typeof details === 'string') {
      try {
        details = JSON.parse(details);
      } catch (e) {
        try {
          details = JSON.parse(details.replace(/'/g, '"'));
        } catch (_) {
          return [];
        }
      }
    }

    if (Array.isArray(details)) {
      return details.filter((item: any) => item && (item.name || item.quantity || item.qty || item.cargoQuantity));
    }
    return [];
  }

  hasCargoDetails(report?: any): boolean {
    const details = this.getCargoDetails(report);
    return details && details.length > 0;
  }

  formatValue(key: string, value: any): string {
    if (!this.hasData(value)) {
      return '-';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    const keyLower = key.toLowerCase();

    // Specific formatting for Cargo Quantity
    if (key === 'cargoQty' || keyLower === 'cargoquantity') {
      const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
      if (!isNaN(num)) {
        return `${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} MT`;
      }
      return `${value} MT`;
    }
    const isDateField =
      keyLower.includes('date') ||
      keyLower.includes('timestamp');

    if (isDateField && typeof value === 'string' && value.length >= 10) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        if (value.includes('T') || value.includes(':')) {
          const pad = (n: number) => String(n).padStart(2, '0');
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const dd = pad(d.getUTCDate());
          const mmm = months[d.getUTCMonth()];
          const yyyy = d.getUTCFullYear();
          const hh = pad(d.getUTCHours());
          const mm = pad(d.getUTCMinutes());
          const ss = pad(d.getUTCSeconds());
          return `${dd}-${mmm}-${yyyy} ${hh}:${mm}:${ss} UTC`;
        }
      }
    }

    return String(value);
  }

  /**
   * Returns categories ordered strictly as in the HTML Master Report Form.
   * Only includes fields that belong to the HTML form AND have actual data.
   */
  getReportCategories(): ReportCategory[] {
    const report = this.data?.report || {};
    const search = this.searchTerm.trim().toLowerCase();
    const categories: ReportCategory[] = [];
    const addedKeys = new Set<string>();

    for (const def of SECTION_DEFS) {
      const fields: ReportField[] = [];
      for (const key of def.keys) {
        // Do NOT show fields already prominently featured in the top header banner
        // (vessel name, IMO, voyage number, operations, operation date/time)
        if (HEADER_PROMOTED_KEYS.has(key)) {
          continue;
        }

        // Intercept merged datetime fields
        if (this.handleMergedDateTimeFields(key, report, addedKeys, search, fields)) {
          continue;
        }

        // Must be a valid HTML form field
        if (!VALID_HTML_FORM_FIELDS.has(key)) continue;
        if (addedKeys.has(key)) continue;

        let val = report[key];
        if (key === 'cargoQty') {
          val = this.getCargoQuantity(report);
        }

        // Only show fields that have actual data
        if (!this.hasData(val) || this.isObject(val)) continue;

        addedKeys.add(key);
        // Also register aliases so we don't display duplicates (e.g. shipName and vesselName)
        if (ALIAS_MAP[key]) {
          ALIAS_MAP[key].forEach((alias) => addedKeys.add(alias));
        }

        const label = this.getLabel(key);
        const formattedVal = this.formatValue(key, val);
        if (
          search &&
          !label.toLowerCase().includes(search) &&
          !String(val).toLowerCase().includes(search) &&
          !formattedVal.toLowerCase().includes(search)
        ) {
          continue;
        }

        fields.push({ key, label, value: val });
      }

      if (fields.length > 0) {
        categories.push({
          id: def.id,
          title: def.title,
          icon: def.icon,
          fields,
        });
      }
    }

    return categories;
  }

  /**
   * Returns Last Report fields ordered strictly as in the HTML Master Report Form.
   * Only includes fields that belong to the HTML form AND have actual data.
   */
  getLastReportFields(): ReportField[] {
    const lastReport = this.data?.last_report || {};
    const search = this.searchTerm.trim().toLowerCase();
    const fields: ReportField[] = [];
    const addedKeys = new Set<string>();

    for (const def of SECTION_DEFS) {
      for (const key of def.keys) {
        // Suppress redundant vessel identity in last report
        if (
          key === 'shipName' ||
          key === 'vesselName' ||
          key === 'imoNumber' ||
          key === 'vesselIMO' ||
          key === 'voyageNumber'
        ) {
          continue;
        }

        if (this.handleMergedDateTimeFields(key, lastReport, addedKeys, search, fields)) {
          continue;
        }

        if (!VALID_HTML_FORM_FIELDS.has(key)) continue;
        if (addedKeys.has(key)) continue;

        let val = lastReport[key];
        if (key === 'cargoQty') {
          val = this.getCargoQuantity(lastReport);
        }

        // Fallback: Extract bunker fields from bunkerGrades array if individual field is empty
        if ((!this.hasData(val) || val === 0) && (lastReport.bunkerGrades || lastReport['bunkerGrades'])) {
          let bGrades = lastReport.bunkerGrades || lastReport['bunkerGrades'];
          if (typeof bGrades === 'string') {
            try {
              bGrades = JSON.parse(bGrades);
            } catch (e) {
              bGrades = [];
            }
          }
          if (Array.isArray(bGrades)) {
            if (key === 'bunkerGrade1') val = bGrades[0]?.grade;
            else if (key === 'quantity1') val = bGrades[0]?.qty;
            else if (key === 'descrepency1') val = bGrades[0]?.desc;
            else if (key === 'remark1') val = bGrades[0]?.remark;
            else if (key === 'bunkerGrade2') val = bGrades[1]?.grade;
            else if (key === 'quantity2') val = bGrades[1]?.qty;
            else if (key === 'descrepency2') val = bGrades[1]?.desc;
            else if (key === 'remark2') val = bGrades[1]?.remark;
            else if (key === 'bunkerGrade3') val = bGrades[2]?.grade;
            else if (key === 'quantity3') val = bGrades[2]?.qty;
            else if (key === 'descrepency3') val = bGrades[2]?.desc;
            else if (key === 'remark3') val = bGrades[2]?.remark;
          }
        }

        if (!this.hasData(val) || this.isObject(val)) continue;

        addedKeys.add(key);
        if (ALIAS_MAP[key]) {
          ALIAS_MAP[key].forEach((alias) => addedKeys.add(alias));
        }

        const label = this.getLabel(key);
        const formattedVal = this.formatValue(key, val);
        if (
          search &&
          !label.toLowerCase().includes(search) &&
          !String(val).toLowerCase().includes(search) &&
          !formattedVal.toLowerCase().includes(search)
        ) {
          continue;
        }

        fields.push({ key, label, value: val });
      }
    }

    return fields;
  }

  getValue(key: string): any {
    return (
      this.data?.report?.[key] ??
      this.data?.report?.rawJson?.[key] ??
      '-'
    );
  }

  getMgoRob(): any {
    const rob =
      this.data?.report?.mgoBunkerRob ??
      this.data?.report?.rawJson?.mgoBunkerRob ??
      this.data?.report?.mgoTotalOldRob ??
      this.data?.report?.rawJson?.mgoTotalOldRob;
    return rob !== undefined && rob !== null && rob !== '' ? rob : '-';
  }

  hasMgoData(): boolean {
    const rep = this.data?.report || {};
    const raw = rep.rawJson || {};
    const keys = [
      'mgoBunkerRob',
      'mgoTotalConsumption',
      'mgoMeConsumption',
      'mgoAeConsumption',
      'mgoBlrConsumption',
      'mgoTotalOldRob',
    ];
    return keys.some((k) => {
      const v = rep[k] ?? raw[k];
      return v !== undefined && v !== null && v !== '' && v !== 0 && v !== '0';
    });
  }
}
